/**
 * 견적서 API 클라이언트 — backend /quotes/* 와 1:1.
 */

import type { ApiResponse } from './types';

const BASE = '/api';

export type QuoteCompany = 'SM' | 'EJ';

/**
 * 견적서 종류 (v2.104.0).
 *   일반    — 품목 표 = 수량·공급단가·공급합계·비고, 총계 2줄
 *   유지보수 — 품목 표 = 수량·단가·월 청구금액·기간(개월)·합계 금액, 총계 3줄
 */
export type QuoteKind = '일반' | '유지보수';

export interface QuoteContactDefaults {
  empId: string;
  name: string;
  title: string;
  displayName: string;
  phone: string;
  email: string;
}

export interface QuoteItemInput {
  itemName: string;
  description: string;
  qty: number;
  unitPrice: number;
  note: string;
  /** 기간(개월) — 유지보수 견적서만 사용. 일반은 항상 1. */
  months: number;
  /**
   * 원가 단가 — **화면 참고용(저장 안 됨)** (v2.235.0). 원가분석표에서 넘어온 줄과 [💡 가격 참고] 로 원가에서 계산한
   * 단가에 붙어, 작성 창이 줄마다 「원가 · 마진율」을 보여 준다. 없거나 null 이면 모름(표시 안 함).
   * 저장 때는 handleSubmit 이 칸을 하나씩 골라 보내므로 서버로 가지 않는다.
   */
  costUnit?: number | null;
}

/** 지난 견적 단가 한 줄 — GET /quotes/item-prices (v2.235.0) */
export interface QuoteItemPriceRow {
  quoteId: number;
  quoteNo: string;
  quoteDate: string;
  companyId: QuoteCompany;
  quoteKind: QuoteKind;
  recipientCompany: string;
  projectName: string;
  itemName: string;
  description: string;
  qty: number;
  unitPrice: number;
  months: number;
}

/**
 * 지난 견적에서 같은 품목을 얼마에 냈나 — 찾는 글자(띄어쓰기로 나눠 모두 포함)로 품목명·상세내용을 찾는다.
 * 단가 0 줄은 빼고 최근 견적 순 · 기본 20줄(최대 50).
 */
export async function searchQuoteItemPrices(q: string, limit = 20): Promise<QuoteItemPriceRow[]> {
  const p = new URLSearchParams({ q: q.trim(), limit: String(limit) });
  const res = await fetch(`${BASE}/quotes/item-prices?${p.toString()}`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<{ rows: QuoteItemPriceRow[] }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.rows;
}

export interface QuoteCreateInput {
  companyId: QuoteCompany;
  quoteKind: QuoteKind;
  quoteDate: string;
  /** 유지보수 계약기간 — '' 면 없음 */
  contractStart: string;
  contractEnd: string;
  recipientCompany: string;
  reference: string;
  projectName: string;
  remark: string;
  /** 📅 견적 유효 (v2.326.0) — 비우면 서버가 「발행 후 1주일」 */
  validity?: string;
  empName?: string;
  empTitle?: string;
  contactPhone?: string;
  contactEmail?: string;
  items: QuoteItemInput[];
  /** 🧮 견적서 작성 창에서 연 원가분석표 — 저장하면 서버가 그 표에 이 견적서 번호를 잇는다 (v2.248.0) */
  costSheetId?: number | null;
}

export interface QuoteUpdateInput {
  companyId?: QuoteCompany;
  quoteKind?: QuoteKind;
  quoteDate?: string;
  contractStart?: string;
  contractEnd?: string;
  recipientCompany?: string;
  reference?: string;
  projectName?: string;
  remark?: string;
  validity?: string;
  empName?: string;
  empTitle?: string;
  contactPhone?: string;
  contactEmail?: string;
  items?: QuoteItemInput[];
  costSheetId?: number | null;
}

export interface QuoteListRow {
  id: number;
  quoteNo: string;
  companyId: QuoteCompany;
  quoteKind: QuoteKind;
  quoteDate: string;
  empId: string | null;
  empName: string;
  empTitle: string;
  empDisplay: string;
  contactPhone: string;
  contactEmail: string;
  recipientCompany: string;
  reference: string;
  projectName: string;
  totalSupply: number;
  totalWithVat: number;
  firstItemName: string;
  firstItemDescription: string;
  itemCount: number;
  /** 연결 영업건 (없으면 null) */
  salesId: string | null;
  /** 이 견적서와 이어진 원가분석표 (v2.236.0 · 표에서 만든 견적서거나 견적서로 만든 표 · 없으면 null) */
  costSheetId: number | null;
  createdBy: string;
  createdAt: string;
}

export interface QuoteDetail extends QuoteListRow {
  remark: string;
  /** 📅 견적 유효 (v2.326.0) — 견적서에 「견 적 유 효 :」로 찍는 글자 · 기본 「발행 후 1주일」 */
  validity: string;
  contractStart: string;
  contractEnd: string;
  updatedAt: string;
  items: Array<{
    id: number;
    sortOrder: number;
    itemName: string;
    description: string;
    qty: number;
    unitPrice: number;
    lineTotal: number;
    note: string;
    months: number;
  }>;
}

export interface QuoteCreateResult {
  id: number;
  quoteNo: string;
  totalSupply: number;
  totalWithVat: number;
}

async function unwrap<T>(r: Response): Promise<T> {
  const json = (await r.json()) as ApiResponse<T>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export async function fetchContactDefaults(): Promise<QuoteContactDefaults> {
  const r = await fetch(`${BASE}/quotes/contact-defaults`, { credentials: 'include' });
  return unwrap<QuoteContactDefaults>(r);
}

/** 영업담당자 4명 전체 리스트 — 견적서 모달 담당 select 옵션 */
export async function fetchContactOptions(): Promise<QuoteContactDefaults[]> {
  const r = await fetch(`${BASE}/quotes/contact-options`, { credentials: 'include' });
  return unwrap<QuoteContactDefaults[]>(r);
}

/** 영업진행에서 distinct 제안업체 목록 (작성 폼 수신 자동완성용) */
export async function fetchProposalCompanies(): Promise<string[]> {
  const r = await fetch(`${BASE}/quotes/lookup/proposal-companies`, { credentials: 'include' });
  const data = await unwrap<{ items: string[] }>(r);
  return data.items;
}

/** 영업진행에서 distinct 거래처담당자 목록 (작성 폼 참조 자동완성용) */
export async function fetchContactPersons(): Promise<string[]> {
  const r = await fetch(`${BASE}/quotes/lookup/contact-persons`, { credentials: 'include' });
  const data = await unwrap<{ items: string[] }>(r);
  return data.items;
}

export async function listQuotes(): Promise<QuoteListRow[]> {
  const r = await fetch(`${BASE}/quotes`, { credentials: 'include' });
  const data = await unwrap<{ quotes: QuoteListRow[] }>(r);
  return data.quotes;
}

export async function getQuote(id: number): Promise<QuoteDetail> {
  const r = await fetch(`${BASE}/quotes/${id}`, { credentials: 'include' });
  return unwrap<QuoteDetail>(r);
}

export async function createQuote(input: QuoteCreateInput): Promise<QuoteCreateResult> {
  const r = await fetch(`${BASE}/quotes`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return unwrap<QuoteCreateResult>(r);
}

export async function updateQuote(id: number, input: QuoteUpdateInput): Promise<void> {
  const r = await fetch(`${BASE}/quotes/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  await unwrap<{ id: number }>(r);
}

export async function deleteQuote(id: number): Promise<void> {
  const r = await fetch(`${BASE}/quotes/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  await unwrap<{ id: number }>(r);
}

export function quoteExcelDownloadUrl(id: number): string {
  return `${BASE}/quotes/${id}/download.xlsx`;
}

export function quotePdfDownloadUrl(id: number): string {
  return `${BASE}/quotes/${id}/download.pdf`;
}

export interface QuoteLinkSalesResult {
  salesId: string;
  action: 'created' | 'updated';
  itemSummary: string;
  proposalPrice: number;
  attached: boolean;
  attachError?: string;
}

export interface QuoteAttachSalesResult {
  salesId: string;
  attached: boolean;
  attachError?: string;
}

/**
 * 견적서를 영업건에 "연결만" (영업건 필드는 그대로) + 견적서 PDF 최신본 재첨부.
 * 영업 상세에서 견적서를 작성/수정할 때 사용 (2026-07-27).
 */
export async function attachQuoteToSales(
  quoteId: number,
  salesId: string,
): Promise<QuoteAttachSalesResult> {
  const r = await fetch(`${BASE}/quotes/${quoteId}/attach-sales`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ salesId }),
  });
  return unwrap<QuoteAttachSalesResult>(r);
}

/** 연동 화면에서 자동완성 표를 편집한 값(선택). 미전달 필드는 견적서 자동매핑값 사용. */
export interface QuoteLinkOverrides {
  proposalCompany?: string | null;
  contactPerson?: string | null;
  projectName?: string | null;
  itemSummary?: string | null;
  endUser?: string | null;
  proposalPrice?: number;
}

/** 견적서 → 영업건 연동 (신규 등록 또는 기존 갱신) + 견적서 PDF 자동 첨부 */
export async function linkQuoteToSales(
  quoteId: number,
  body:
    | { mode: 'new'; overrides?: QuoteLinkOverrides }
    | { mode: 'update'; targetSalesId: string; overrides?: QuoteLinkOverrides },
): Promise<QuoteLinkSalesResult> {
  const r = await fetch(`${BASE}/quotes/${quoteId}/link-sales`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return unwrap<QuoteLinkSalesResult>(r);
}

// ── 엑셀 업로드 → 견적 작성 폼 초안 파싱 ───────────────────────
export interface QuoteDraft {
  quoteDate: string | null;
  recipientCompany: string;
  reference: string;
  projectName: string;
  empName: string;
  contactPhone: string;
  contactEmail: string;
  items: QuoteItemInput[];
  /** 사업자 — 엑셀 서식으로는 판별 불가(백엔드 미전달). 기존 견적서 복사 시에만 채워진다. */
  companyId?: QuoteCompany;
  /** Remark — 기존 견적서 복사 시에만 채워진다. */
  remark?: string;
  /** 종류 — 엑셀은 표 머리글로 판별, 복사는 원본 그대로. */
  quoteKind?: QuoteKind;
  /** 유지보수 계약기간 — 기존 견적서 복사 시에만 채워진다. */
  contractStart?: string;
  contractEnd?: string;
  /** 📅 견적 유효 (v2.326.0) — 엑셀은 C9 칸, 복사는 원본 그대로. 없으면 작성 창이 「발행 후 1주일」 */
  validity?: string;
}

export interface QuoteParseResult {
  draft: QuoteDraft;
  warnings: string[];
}

/**
 * 저장된 견적서 → 새 견적서 작성 초안 (2026-07-30).
 *
 * "이 견적서 내용으로 새로 작성" 기능. 내용은 전부 그대로 가져오되
 * 견적일자는 비워 둔다 → 작성 폼이 오늘 날짜로 채운다 (견적번호는 저장 시 새로 발번됨).
 */
export function quoteDetailToDraft(detail: QuoteDetail): QuoteDraft {
  return {
    companyId: detail.companyId,
    quoteKind: detail.quoteKind,
    quoteDate: null,
    contractStart: detail.contractStart ?? '',
    contractEnd: detail.contractEnd ?? '',
    recipientCompany: detail.recipientCompany,
    reference: detail.reference,
    projectName: detail.projectName,
    empName: detail.empDisplay ?? '',
    contactPhone: detail.contactPhone ?? '',
    contactEmail: detail.contactEmail ?? '',
    remark: detail.remark ?? '',
    validity: detail.validity ?? '',
    items: detail.items.map((it) => ({
      itemName: it.itemName,
      description: it.description,
      qty: it.qty,
      unitPrice: it.unitPrice,
      note: it.note,
      months: it.months ?? 1,
    })),
  };
}

/** 포털 견적 템플릿 서식의 .xlsx 업로드 → 인식된 초안 + 경고 반환 (DB 저장 X) */
export async function parseQuoteXlsx(file: File): Promise<QuoteParseResult> {
  const fd = new FormData();
  fd.append('file', file);
  const r = await fetch(`${BASE}/quotes/parse-xlsx`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  return unwrap<QuoteParseResult>(r);
}

/**
 * 총계 자동 계산 — 클라이언트 미리보기용. 백엔드 calcTotals 와 같은 식.
 *   lineTotals   = 수량 × 단가            (유지보수에선 「월 청구금액」)
 *   totalSupply  = Σ(lineTotal × months)  (유지보수에선 「계약기간 총계 VAT 별도」)
 *   totalMonthly = Σ lineTotal            (유지보수 「월 청구금액 총계」)
 * 일반은 months 를 1 로 보므로 종전과 값이 같다.
 */
export function computeQuoteTotals(
  items: Pick<QuoteItemInput, 'qty' | 'unitPrice' | 'months'>[],
  quoteKind: QuoteKind = '일반',
): {
  totalSupply: number;
  totalWithVat: number;
  totalMonthly: number;
  lineTotals: number[];
  periodTotals: number[];
} {
  const isMaint = quoteKind === '유지보수';
  let totalSupply = 0;
  let totalMonthly = 0;
  const lineTotals: number[] = [];
  const periodTotals: number[] = [];
  for (const it of items) {
    const months = isMaint ? Math.max(1, Math.round(Number(it.months) || 1)) : 1;
    const lt = Math.round((Number(it.qty) || 0) * (Number(it.unitPrice) || 0));
    lineTotals.push(lt);
    periodTotals.push(lt * months);
    totalMonthly += lt;
    totalSupply += lt * months;
  }
  return {
    totalSupply,
    totalWithVat: Math.round(totalSupply * 1.1),
    totalMonthly,
    lineTotals,
    periodTotals,
  };
}

/**
 * 견적서 템플릿 상세내용 열 너비 (Excel column.width 단위).
 * 1 단위 ≒ Calibri 11 영문 한 글자 폭 ≒ 약 7 픽셀.
 * 유지보수 양식은 기간·합계 금액 칸이 붙는 만큼 상세내용 칸이 좁다.
 */
export const QUOTE_DESC_COL_WIDTH: Record<QuoteKind, Record<QuoteCompany, number>> = {
  일반: { SM: 40.66, EJ: 54.33 },
  유지보수: { SM: 38.78, EJ: 40.78 },
};

function descColWidth(companyId: QuoteCompany, quoteKind: QuoteKind): number {
  return QUOTE_DESC_COL_WIDTH[quoteKind][companyId];
}

/** Excel 열 너비 단위 → CSS 픽셀 추정. 작성 폼의 textarea max-width 결정에 사용 */
export function quoteDescColWidthPx(
  companyId: QuoteCompany,
  quoteKind: QuoteKind = '일반',
): number {
  return Math.round(descColWidth(companyId, quoteKind) * 7);
}

/**
 * Remark 본문 — 유지보수는 계약기간 줄을 맨 위에 얹는다.
 * 백엔드 buildQuoteRemark 와 같은 문구 (미리보기가 실제 문서와 어긋나지 않게).
 */
export function buildQuoteRemark(head: {
  quoteKind?: QuoteKind | null;
  contractStart?: string | null;
  contractEnd?: string | null;
  remark?: string | null;
}): string {
  const body = (head.remark ?? '').trim();
  if (head.quoteKind !== '유지보수') return body;
  const s = (head.contractStart ?? '').trim();
  const e = (head.contractEnd ?? '').trim();
  if (!s && !e) return body;
  const dot = (v: string) => v.replace(/-/g, '.');
  const period = s && e ? `${dot(s)} ~ ${dot(e)}` : dot(s || e);
  const line = `* 계약기간 : ${period}`;
  return body ? `${line}\n${body}` : line;
}

/**
 * 📅 견적 유효 (v2.326.0 · 사용자 「유효기간도 기본 1주일은 유지하고 기간을 수정도 할 수 있도록」) — 새 견적서의 처음 값.
 * 서버(backend/src/routes/quotes/validity.ts)도 비어 있으면 같은 글자로 채운다.
 */
export const QUOTE_VALIDITY_DEFAULT = '발행 후 1주일';
/** 작성 창의 빠른 고르기 단추 — 누르면 「발행 후 …」로 채운다(칸에 직접 적어도 된다) */
export const QUOTE_VALIDITY_PRESETS = ['1주일', '2주일', '1개월'] as const;

/**
 * 유지보수 견적서 Remark 기본 문구 (사용자 결정 2026-08-28 · 회사별로 다름).
 * 「유지보수」를 처음 고를 때 한 번만 채워 넣고, 이후 사람이 지우거나 고칠 수 있다.
 * 계약기간 줄은 여기 넣지 않는다 — 날짜 칸에서 자동으로 붙는다.
 */
export const MAINTENANCE_REMARK_DEFAULT: Record<QuoteCompany, string> = {
  SM: '* 월 1회 정기점검 및 리포트 제출, 장애시 긴급지원, 보증기간 내 파트교체 지원',
  EJ: '* 월 1회 정기점검 및 리포트 제출, 장애시 긴급지원, 파트교체비용 포함',
};

/** CJK 시각적 너비 (한글·한자·전각 = 2, 그 외 = 1) */
function visualWidth(s: string): number {
  let w = 0;
  for (const ch of s) {
    const code = ch.codePointAt(0) ?? 0;
    if (
      (code >= 0xac00 && code <= 0xd7af) ||
      (code >= 0x3000 && code <= 0x9fff) ||
      (code >= 0xff00 && code <= 0xffef) ||
      (code >= 0x1100 && code <= 0x11ff)
    ) {
      w += 2;
    } else {
      w += 1;
    }
  }
  return w;
}

/** 백엔드 estimateLines 와 동일 — 작성 폼에서 "엑셀 N줄" 안내용 */
export function estimateQuoteLines(
  text: string,
  companyId: QuoteCompany,
  quoteKind: QuoteKind = '일반',
): number {
  if (!text) return 1;
  const colWidth = descColWidth(companyId, quoteKind);
  const usable = Math.max(4, colWidth * 0.92);
  let total = 0;
  for (const line of text.split('\n')) {
    const w = visualWidth(line);
    total += Math.max(1, Math.ceil(w / usable));
  }
  return total;
}
